import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { feedbackPortalAPI } from '../services/api';

// ── Score color ────────────────────────────────────────────────
function scoreHex(n) {
  if (!n) return '#94a3b8';
  const pct = (n - 1) / 9;
  const r = Math.round(239 - pct * (239 - 16));
  const g = Math.round(68  + pct * (185 - 68));
  const b = Math.round(68  - pct * (68  - 16));
  return `rgb(${r},${g},${b})`;
}

// ── 10-button score input ──────────────────────────────────────
function ScoreInput({ question, value, onChange, accent }) {
  return (
    <div className="mb-7">
      <p className="text-base font-medium text-gray-800 mb-3">{question}</p>
      <div className="grid grid-cols-10 gap-1">
        {Array.from({ length: 10 }, (_, i) => i + 1).map(n => {
          const sel = parseInt(value) === n;
          const col = scoreHex(n);
          return (
            <button key={n} type="button" onClick={() => onChange(String(n))}
              style={{ backgroundColor: sel ? col : 'white', borderColor: col, color: sel ? 'white' : col, transform: sel ? 'scale(1.15)' : 'scale(1)', boxShadow: sel ? `0 4px 14px ${col}55` : 'none' }}
              className="h-11 rounded-xl border-2 text-sm font-bold transition-all duration-150 active:scale-95">
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-gray-400 mt-1.5 px-0.5">
        <span>Poor (1)</span><span>Excellent (10)</span>
      </div>
    </div>
  );
}

// ── Multi-select chips ─────────────────────────────────────────
function ChipSelect({ options = [], selected = [], onChange, color = 'blue' }) {
  const toggle = v => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const c = color === 'amber'
    ? { sel:'bg-amber-500 text-white border-amber-500', unsel:'bg-white text-gray-600 border-gray-200 hover:border-amber-400' }
    : { sel:'bg-blue-600 text-white border-blue-600', unsel:'bg-white text-gray-600 border-gray-200 hover:border-blue-400' };
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {options.map(o => (
        <button key={o} type="button" onClick={() => toggle(o)}
          className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all duration-150 ${selected.includes(o) ? c.sel : c.unsel}`}>
          {selected.includes(o) && <span className="mr-1">✓</span>}{o}
        </button>
      ))}
    </div>
  );
}

// ── Yes / No input ─────────────────────────────────────────────
function YesNoInput({ question, value, onChange, accent }) {
  const opts = [{ v:'yes', label:'Yes', icon:'✅' }, { v:'no', label:'No', icon:'❌' }];
  return (
    <div className="mb-4">
      {question && <p className="text-base font-medium text-gray-800 mb-4">{question}</p>}
      <div className="flex gap-4 justify-center">
        {opts.map(o => (
          <button key={o.v} type="button" onClick={() => onChange(o.v)}
            style={value === o.v ? { borderColor: accent, backgroundColor: accent + '18', color: accent } : {}}
            className={`flex-1 max-w-[160px] py-5 rounded-2xl border-2 text-center transition-all font-bold text-lg ${value === o.v ? 'shadow-md' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
            <div className="text-3xl mb-1">{o.icon}</div>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Default questions (fallback when campaign has none) ────────
const DEFAULT_QUESTIONS = [
  { id:'q1', type:'score',       field:'academic_progress',    emoji:'📚', title:'Academic Progress',       subtitle:'How well is the school supporting your child academically?',     question:"Overall, how satisfied are you with your child's academic progress this term?",                            required:true  },
  { id:'q2', type:'score',       field:'teacher_effectiveness',emoji:'👩‍🏫', title:'Teacher Effectiveness',   subtitle:"Rate your child's class teacher",                               question:'How effectively does the teacher support and engage your child?',                                           required:true  },
  { id:'q3', type:'score',       field:'communication',        emoji:'💬', title:'School Communication',    subtitle:'How well does the school keep you informed?',                   question:"How satisfied are you with the school's communication with parents?",                                        required:true  },
  { id:'q4', type:'score',       field:'student_development',  emoji:'🌱', title:'Student Development',     subtitle:'Beyond academics — confidence, skills, character',              question:"How well is the school developing your child's overall personality and skills?",                             required:true  },
  { id:'q5', type:'score',       field:'school_services',      emoji:'🏫', title:'School Services',         subtitle:'Infrastructure, transport, activities, facilities',             question:'How satisfied are you with school services (transport, facilities, co-curricular)?',                        required:false },
  { id:'q6', type:'score',       field:'recommendation',       emoji:'⭐', title:'Recommendation',          subtitle:'Would you recommend this school to others?',                    question:'On a scale of 1–10, how likely are you to recommend this school to other parents?',                         required:true  },
  { id:'q7', type:'multiselect', field:'improvement_areas',    emoji:'🎯', title:'Child Improvement Areas', subtitle:"Select all areas where you'd like to see growth",              options:['Reading Skills','Writing Skills','Mathematics','Communication','Public Speaking','Confidence Building','Critical Thinking','Focus / Attention','Behaviour / Discipline','Social Skills','Leadership','Responsibility'], required:false, color:'blue'  },
  { id:'q8', type:'multiselect', field:'concern_categories',   emoji:'⚠️', title:'Areas of Concern',        subtitle:'Select any concerns you have — we take these seriously',       options:['Academic Support','Communication Gap','Homework Load','Transport / Safety','Infrastructure','Co-curricular Activities','Technology Usage','Food / Canteen','Discipline','Other'], required:false, color:'amber' },
  { id:'q9', type:'open',        field:'open_feedback',        emoji:'✍️', title:'Open Feedback',           subtitle:'Your words matter — please share freely',                      required:false },
];

// ── Template themes ────────────────────────────────────────────
const THEMES = {
  classic: { pageBg:'#f1f5f9', hdrGrad:'135deg,#3b82f6,#6366f1', accent:'#3b82f6', btnText:'#ffffff' },
  ocean:   { pageBg:'#eff6ff', hdrGrad:'135deg,#1e40af,#3b82f6', accent:'#1e40af', btnText:'#ffffff' },
  amber:   { pageBg:'#fffbeb', hdrGrad:'135deg,#d97706,#f59e0b', accent:'#d97706', btnText:'#ffffff' },
  forest:  { pageBg:'#ecfdf5', hdrGrad:'135deg,#065f46,#059669', accent:'#059669', btnText:'#ffffff' },
  purple:  { pageBg:'#f5f3ff', hdrGrad:'135deg,#5b21b6,#7c3aed', accent:'#7c3aed', btnText:'#ffffff' },
};

// ── Field classification helpers ───────────────────────────────
const SCORE_COLS = new Set(['academic_progress','teacher_effectiveness','communication','student_development','school_services','recommendation']);
const MS_COLS    = new Set(['improvement_areas','concern_categories']);

function buildBlank(qs) {
  const f = { parent_name:'', appreciate_text:'', improve_text:'', management_concern:'' };
  qs.forEach(q => {
    if (q.type === 'multiselect') f[q.field] = [];
    else if (q.type !== 'open') f[q.field] = '';
  });
  return f;
}

function buildPayload(form, qs) {
  const p = { parent_name: form.parent_name || '', custom_answers: {} };
  qs.forEach(q => {
    if (q.field === 'open_feedback') {
      p.appreciate_text    = form.appreciate_text    || '';
      p.improve_text       = form.improve_text       || '';
      p.management_concern = form.management_concern || '';
    } else if (SCORE_COLS.has(q.field)) {
      p[q.field] = form[q.field] || null;
    } else if (MS_COLS.has(q.field)) {
      p[q.field] = form[q.field] || [];
    } else {
      p.custom_answers[q.field] = form[q.field];
    }
  });
  return p;
}

// ── Main component ─────────────────────────────────────────────
export default function FeedbackPortal() {
  const { token } = useParams();
  const [state,  setState]  = useState('loading');
  const [info,   setInfo]   = useState(null);
  const [form,   setForm]   = useState({ parent_name:'' });
  const [step,   setStep]   = useState(0);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const load = useCallback(async () => {
    try {
      const r = await feedbackPortalAPI.getByToken(token);
      const d = r.data;
      setInfo(d);
      if (d.is_closed) { setState('closed'); return; }
      if (d.existing?.status === 'submitted') { setState('submitted'); return; }

      const qs = d.campaign?.questions || DEFAULT_QUESTIONS;
      const blank = buildBlank(qs);

      if (d.existing) {
        const restored = { ...blank, parent_name: d.existing.parent_name || d.student?.parent_name || '' };
        qs.forEach(q => {
          if (q.field === 'open_feedback') {
            restored.appreciate_text    = d.existing.appreciate_text    || '';
            restored.improve_text       = d.existing.improve_text       || '';
            restored.management_concern = d.existing.management_concern || '';
          } else if (SCORE_COLS.has(q.field)) {
            restored[q.field] = d.existing[q.field] ? String(d.existing[q.field]) : '';
          } else if (MS_COLS.has(q.field)) {
            restored[q.field] = d.existing[q.field] || [];
          } else if (d.existing.custom_answers?.[q.field] !== undefined) {
            restored[q.field] = d.existing.custom_answers[q.field];
          }
        });
        setForm(restored);
      } else {
        setForm({ ...blank, parent_name: d.student?.parent_name || '' });
      }
      setState('form');
    } catch (e) {
      setError(e.response?.data?.error || 'This link is invalid or has expired.');
      setState('error');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const questions   = info?.campaign?.questions || DEFAULT_QUESTIONS;
  const layout      = info?.campaign?.form_layout || {};
  const theme       = THEMES[layout.template] || THEMES.classic;
  const totalSteps  = questions.length;
  const CONFIRM_STEP = totalSteps + 1;

  const setField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const saveDraft = async (data = form) => {
    try { await feedbackPortalAPI.saveDraft(token, buildPayload(data, questions)); } catch {}
  };

  const handleNext = async () => {
    if (step < totalSteps) { await saveDraft(); setStep(s => s + 1); window.scrollTo(0, 0); }
    else { setStep(CONFIRM_STEP); window.scrollTo(0, 0); }
  };

  const handleBack = () => { setStep(s => Math.max(0, s - 1)); window.scrollTo(0, 0); };

  const handleSubmit = async () => {
    setSaving(true); setError('');
    try {
      await feedbackPortalAPI.submit(token, buildPayload(form, questions));
      setState('submitted'); window.scrollTo(0, 0);
    } catch (e) { setError(e.response?.data?.error || 'Submission failed. Please try again.'); }
    setSaving(false);
  };

  // ── Static screens ──────────────────────────────────────────
  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"/>
          <p className="text-gray-500 text-sm">Loading your feedback form…</p>
        </div>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-rose-100 p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Not Found</h2>
          <p className="text-gray-500 text-sm">{error}</p>
          <p className="text-gray-400 text-xs mt-4">Please contact the school for a new feedback link.</p>
        </div>
      </div>
    );
  }
  if (state === 'closed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-gray-100 p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Feedback Closed</h2>
          <p className="text-gray-500 text-sm">The feedback window for this campaign has ended.</p>
        </div>
      </div>
    );
  }
  if (state === 'submitted') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: theme.pageBg }}>
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-black mb-2" style={{ color: theme.accent }}>Thank You!</h2>
          <p className="text-gray-700 font-medium">{info?.student?.parent_name || 'Dear Parent'},</p>
          <p className="text-gray-500 text-sm mt-2 leading-relaxed">
            Your feedback for <strong>{info?.student?.name}</strong> ({info?.student?.class_name}) has been successfully submitted.
          </p>
          <p className="text-gray-400 text-xs mt-4">Your responses help us improve. We appreciate your time.</p>
          {info?.school?.name && <p className="text-gray-300 text-xs mt-6 font-medium">{info.school.name}</p>}
        </div>
      </div>
    );
  }

  const { campaign, student, school } = info;
  const BASE_URL  = window.location.origin;
  const logoSrc   = layout.logoUrl || (school?.logo ? `${BASE_URL}${school.logo}` : null);
  const hdrTitle  = layout.headerTitle  || school?.name || 'The Kulish School';
  const hdrSub    = layout.headerSubtitle || campaign.name;
  const showProg  = layout.showProgress !== false;

  // ── Welcome screen ───────────────────────────────────────────
  if (step === 0) {
    return (
      <div className="min-h-screen flex items-start justify-center p-4 pt-8" style={{ background: theme.pageBg }}>
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="px-6 pt-8 pb-6 text-white text-center" style={{ background: `linear-gradient(${theme.hdrGrad})` }}>
            {logoSrc ? (
              <img src={logoSrc} alt="logo" className="w-16 h-16 mx-auto rounded-full bg-white p-1 mb-3 object-contain" onError={e => { e.target.style.display='none'; }}/>
            ) : (
              <div className="w-16 h-16 mx-auto rounded-full bg-white/20 flex items-center justify-center text-3xl mb-3">🎓</div>
            )}
            <h1 className="text-xl font-black">{hdrTitle}</h1>
            <p className="text-white/70 text-sm mt-1">{hdrSub}</p>
          </div>

          <div className="p-6">
            <div className="rounded-2xl p-4 mb-6" style={{ backgroundColor: theme.accent + '15' }}>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: theme.accent }}>Student Details</p>
              <p className="font-bold text-gray-900 text-lg">{student.name}</p>
              <p className="text-gray-500 text-sm">{student.class_name}{student.class_teacher ? ` · ${student.class_teacher}` : ''}</p>
              {student.admission_no && <p className="text-gray-400 text-xs mt-0.5">Adm. {student.admission_no}</p>}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Your Name (Parent / Guardian)</label>
              <input type="text" value={form.parent_name} onChange={e => setField('parent_name', e.target.value)}
                placeholder="Enter your name"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': theme.accent + '80' }}/>
            </div>

            <p className="text-xs text-gray-400 mb-6 text-center">
              This form has <strong>{totalSteps} section{totalSteps !== 1 ? 's' : ''}</strong> and takes about <strong>5–7 minutes</strong>. Your draft saves automatically.
            </p>

            {layout.footerNote && <p className="text-xs text-gray-400 mb-5 text-center leading-relaxed">{layout.footerNote}</p>}

            <button onClick={() => { setStep(1); window.scrollTo(0, 0); }}
              style={{ backgroundColor: theme.accent, color: theme.btnText }}
              className="w-full font-bold py-4 rounded-2xl text-base transition-all shadow-lg active:scale-[0.98] hover:opacity-90">
              Start Feedback →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Confirmation screen ──────────────────────────────────────
  if (step === CONFIRM_STEP) {
    return (
      <div className="min-h-screen p-4" style={{ background: theme.pageBg }}>
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
            <h2 className="text-lg font-black text-gray-900 mb-1">Review & Submit</h2>
            <p className="text-sm text-gray-400 mb-5">Please review your responses before submitting.</p>

            {/* Score summaries */}
            {questions.filter(q => q.type === 'score' && form[q.field]).length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-5">
                {questions.filter(q => q.type === 'score').map(q => (
                  <div key={q.field} className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-gray-500 leading-tight mb-1">{q.title}</p>
                    <p className="text-xl font-black" style={{ color: scoreHex(parseInt(form[q.field]) || 0) }}>
                      {form[q.field] || '—'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Yes/No summaries */}
            {questions.filter(q => q.type === 'yesno' && form[q.field]).map(q => (
              <div key={q.field} className="mb-3">
                <p className="text-xs font-semibold text-gray-400 mb-1">{q.title}</p>
                <span className={`text-sm font-bold ${form[q.field]==='yes'?'text-emerald-600':'text-red-500'}`}>
                  {form[q.field] === 'yes' ? '✅ Yes' : '❌ No'}
                </span>
              </div>
            ))}

            {/* Multi-select summaries */}
            {questions.filter(q => q.type === 'multiselect').map(q => (
              (form[q.field] || []).length > 0 && (
                <div key={q.field} className="mb-3">
                  <p className="text-xs font-semibold text-gray-400 mb-1.5">{q.title}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(form[q.field] || []).map(a => (
                      <span key={a} className={`text-xs px-2 py-1 rounded-full ${q.color==='amber'?'bg-amber-50 text-amber-700':'bg-blue-50 text-blue-700'}`}>{a}</span>
                    ))}
                  </div>
                </div>
              )
            ))}

            {/* Custom field summaries */}
            {questions.filter(q => !SCORE_COLS.has(q.field) && !MS_COLS.has(q.field) && q.field !== 'open_feedback' && q.type !== 'yesno' && q.type !== 'multiselect' && form[q.field]).map(q => (
              <div key={q.field} className="mb-3">
                <p className="text-xs font-semibold text-gray-400 mb-1">{q.title}</p>
                <p className="text-sm text-gray-700 line-clamp-2">{form[q.field]}</p>
              </div>
            ))}
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3 mb-4">{error}</div>}

          <div className="flex gap-3">
            <button onClick={handleBack}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 rounded-2xl text-sm transition-colors">
              ← Edit
            </button>
            <button onClick={handleSubmit} disabled={saving}
              style={{ backgroundColor: theme.accent, color: theme.btnText }}
              className="flex-1 font-bold py-4 rounded-2xl text-sm transition-all disabled:opacity-60 shadow-md">
              {saving ? 'Submitting…' : '✓ Submit Feedback'}
            </button>
          </div>
          <p className="text-xs text-center text-gray-400 mt-3">Once submitted, your response cannot be changed.</p>
        </div>
      </div>
    );
  }

  // ── Question screens (steps 1 – totalSteps) ──────────────────
  const q = questions[step - 1];
  if (!q) return null;
  const progress = (step / totalSteps) * 100;

  const canProceed = () => {
    if (!q.required) return true;
    if (q.type === 'score') return !!form[q.field];
    if (q.type === 'yesno') return !!form[q.field];
    return true;
  };

  return (
    <div className="min-h-screen" style={{ background: theme.pageBg }}>

      {/* Sticky progress header */}
      {showProg && (
        <div className="sticky top-0 bg-white border-b border-gray-100 shadow-sm z-10">
          <div className="max-w-md mx-auto px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-400 font-medium">{student.name} · {student.class_name}</p>
              <p className="text-xs font-bold" style={{ color: theme.accent }}>{step} / {totalSteps}</p>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: theme.accent }}/>
            </div>
          </div>
        </div>
      )}

      {/* Section content */}
      <div className="max-w-md mx-auto p-4 pt-6 pb-32">
        {/* Section header */}
        <div className="text-center mb-6">
          <span className="text-4xl">{q.emoji}</span>
          <h2 className="text-xl font-black text-gray-900 mt-2">{q.title}</h2>
          {q.subtitle && <p className="text-sm text-gray-500 mt-1">{q.subtitle}</p>}
        </div>

        {/* Score */}
        {q.type === 'score' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <ScoreInput question={q.question} value={form[q.field]} onChange={v => setField(q.field, v)} accent={theme.accent}/>
            {!form[q.field] && q.required && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mt-2">Please select a score to continue</p>
            )}
            {!q.required && <p className="text-xs text-gray-400 mt-1">Optional — skip if not applicable</p>}
          </div>
        )}

        {/* Multi-select */}
        {q.type === 'multiselect' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-sm font-medium text-gray-700 mb-1">Select all that apply:</p>
            <p className="text-xs text-gray-400 mb-3">You can select multiple options</p>
            <ChipSelect options={q.options || []} selected={form[q.field] || []} onChange={v => setField(q.field, v)} color={q.color}/>
            <p className="text-xs text-gray-400 mt-3">Optional — skip if none apply</p>
          </div>
        )}

        {/* Yes / No */}
        {q.type === 'yesno' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <YesNoInput question={q.question} value={form[q.field]} onChange={v => setField(q.field, v)} accent={theme.accent}/>
            {!form[q.field] && q.required && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mt-2">Please select an answer to continue</p>
            )}
          </div>
        )}

        {/* Open feedback — classic 3-part (open_feedback virtual field) */}
        {q.type === 'open' && q.field === 'open_feedback' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm font-semibold text-emerald-700 mb-1">💚 What do you appreciate most?</p>
              <p className="text-xs text-gray-400 mb-2">Tell us what the school does well</p>
              <textarea value={form.appreciate_text} onChange={e => setField('appreciate_text', e.target.value)}
                rows={3} placeholder="The teachers are very dedicated…"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300"/>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm font-semibold text-amber-700 mb-1">🔧 What could be improved?</p>
              <p className="text-xs text-gray-400 mb-2">Your suggestions help us get better</p>
              <textarea value={form.improve_text} onChange={e => setField('improve_text', e.target.value)}
                rows={3} placeholder="Better communication about homework…"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-300"/>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-5">
              <p className="text-sm font-semibold text-red-700 mb-1">⚠️ Specific concern for management?</p>
              <p className="text-xs text-gray-400 mb-2">This will be reviewed by senior management only</p>
              <textarea value={form.management_concern} onChange={e => setField('management_concern', e.target.value)}
                rows={3} placeholder="Leave blank if no specific concern…"
                className="w-full border border-red-100 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-200"/>
            </div>
          </div>
        )}

        {/* Open feedback — simple single textarea for custom open fields */}
        {q.type === 'open' && q.field !== 'open_feedback' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            {q.question && <p className="text-sm font-medium text-gray-700 mb-3">{q.question}</p>}
            <textarea value={form[q.field] || ''} onChange={e => setField(q.field, e.target.value)}
              rows={5} placeholder="Share your thoughts…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2"/>
            <p className="text-xs text-gray-400 mt-2">Optional — skip if you have nothing to add</p>
          </div>
        )}
      </div>

      {/* Fixed bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
        <div className="max-w-md mx-auto px-4 py-3 flex gap-3">
          <button onClick={step <= 1 ? () => setStep(0) : handleBack}
            className="w-20 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3.5 rounded-2xl text-sm transition-colors">
            ← Back
          </button>
          <button onClick={handleNext} disabled={!canProceed()}
            style={canProceed() ? { backgroundColor: theme.accent, color: theme.btnText } : {}}
            className="flex-1 font-bold py-3.5 rounded-2xl text-sm transition-all shadow-md disabled:bg-gray-200 disabled:text-gray-400">
            {step === totalSteps ? 'Review →' : 'Next →'}
          </button>
        </div>
        {step === totalSteps && (
          <div className="max-w-md mx-auto px-4 pb-2">
            <button onClick={() => saveDraft()}
              className="w-full text-xs text-gray-400 hover:text-gray-600 py-1 transition-colors">
              💾 Save draft &amp; continue later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
